import { excludeNullOrUndefined } from 'checkout/utils'
import config from 'constants/config'
import { sum } from 'lib/arrayUtils'
import { exceedsTicketAvailability } from 'lib/checkout/experiences/cart'
import { getExperienceTimesKey } from 'lib/experienceUtils'

function calculateValue(price: number, discountPercentage: number) {
  const priceFraction = 1 - (discountPercentage / 100)
  if (priceFraction === 0) { return 0 }
  return price / priceFraction
}

function calculateDiscount(price: number, value: number) {
  if (value === 0) { return 0 }
  const discountFraction = 1 - (price / value)
  return Math.round(100 * discountFraction)
}

export function mapExperienceToView(
  exp: App.ExperienceOffer,
  cartItem: App.Checkout.ExperienceItem,
  timeSlotTickets: App.ExperienceItemTicket[],
): App.Checkout.ExperienceItemView {
  const ticketViews = cartItem.tickets.map(ticketItem => {
    const ticket = timeSlotTickets.find(t => t.id === ticketItem.ticketId)
    return mapExperienceTicketToView(ticketItem, ticket)
  }).filter(excludeNullOrUndefined)

  const price = sum(ticketViews, tv => tv.price)
  const value = sum(ticketViews, tv => tv.value)
  return {
    itemId: cartItem.itemId,
    id: exp.id,
    type: exp.productType,
    experienceId: exp.id,
    primaryCategory: (exp.categories.find(cat => cat.level === 'parent') ?? exp.categories[0])?.name,
    categories: exp.categories,
    location: exp.location.name,
    latitude: exp.location.latitude,
    longitude: exp.location.longitude,
    externalImage: exp.images[0],
    price,
    value,
    discount: calculateDiscount(price, value),
    propertyFees: 0,
    taxesAndFees: 0,
    surcharge: 0,
    title: exp.name,
    ticketViews,
    leExclusive: exp.leExclusive && config.BRAND === 'luxuryescapes',
    freeCancellation: exp.cancellationPolicies.length > 0,
    pickupPoint: cartItem.pickupPointId ? exp.pickupPoints.find(pp => pp.id === cartItem.pickupPointId) : undefined,
    language: cartItem.languageId ? exp.languages.find(l => l.id === cartItem.languageId) : undefined,
    bookingDate: exp.hasCalendar ? cartItem.date : undefined,
    bookingTime: cartItem.time,
    cancellationPolicies: exp.cancellationPolicies,
    cancellationInfo: exp.copy.cancellationInfo,
    hasCalendar: exp.hasCalendar,
    hasTimeslots: exp.hasTimeslots,
    designation: 'Experiences',
    redemptionLocation: cartItem.redemptionLocationId ?
      exp.redemptionLocations.find(rl => rl.id === cartItem.redemptionLocationId) : undefined,
    isBuyNowBookLater: cartItem.isBuyNowBookLater,
    isGift: cartItem.isGift,
    expirationDate: exp.expirationDate,
    bookByDate: exp.bookByDate,
  }
}

function mapExperienceTicketToView(
  cartTicketItem: App.Checkout.ExperienceItemTicket,
  ticket: App.ExperienceItemTicket | undefined,
): App.Checkout.ExperienceItemView['ticketViews'][number] | null {
  const { ticketId, count } = cartTicketItem

  const price = (ticket?.price ?? 0) * cartTicketItem.count
  const discount = (ticket?.discount ?? 0)

  if (cartTicketItem.count === 0) { return null }

  const exceededLimit = ticket ?
      (exceedsTicketAvailability(count, ticket.availability) || count > ticket.max) :
    false

  return {
    id: ticketId,
    name: ticket?.name ?? cartTicketItem.name,
    fareType: ticket?.fareType ?? '',
    price,
    value: calculateValue(price, discount),
    discount,
    count,
    /*
      TODO in future: Give some more information about why the ticket is unavailable. e.g. is it:
      - Actually sold out
      - Not sold out but insufficient availability for the quantity selected
      - Ticket not found
    */
    unavailable: !ticket || exceededLimit,
    min: ticket?.min ?? 0,
  }
}

export function getExperienceItemView(
  item: App.Checkout.ExperienceItem,
  experiences: Record<string, App.ExperienceOffer>,
  experienceTimes: {[id: string]: App.ExperienceTimeSlotsState},
  currencyCode: string,
): App.Checkout.ExperienceItemView | undefined {
  const experience = experiences[item.experienceId]

  const key = getExperienceTimesKey(item.experienceId, item.date, {
    currency: currencyCode,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
  })

  const timeSlots = experienceTimes[item.experienceId]?.[key]?.slots
  const tickets = timeSlots?.flatMap(slot => slot.tickets)

  if (experience && tickets) {
    return mapExperienceToView(experience, item, tickets)
  }
}
